import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Clock } from "lucide-react";
import { Reveal } from "./Reveal";
import { POSTS } from "../../data/content";

export const Blog = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="blog" data-testid="blog-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase font-mono tracking-[0.28em] text-emerald-600 dark:text-emerald-400 font-semibold mb-4">Blog técnico</p>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-xl">
              Contabilidad explicada <span className="font-serif italic text-emerald-500">sin enredos</span>
            </h2>
            <p className="text-sm text-muted-foreground max-w-sm">Artículos prácticos escritos por nuestros docentes, actualizados con cada cambio normativo.</p>
          </div>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {POSTS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className="h-full">
              <button
                data-testid={`blog-card-${p.id}`}
                onClick={() => setActive(p)}
                className="group text-left w-full h-full rounded-3xl border border-border bg-card overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(16,185,129,0.12)] hover:border-emerald-500/40"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <span className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-widest bg-black/55 backdrop-blur text-white rounded-full px-3 py-1">{p.category}</span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground font-mono">
                    <span>{p.date}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.readTime}</span>
                  </div>
                  <h3 className="font-display text-lg font-semibold tracking-tight mt-3 leading-snug flex-1">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-2.5">{p.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                    Leer artículo
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="blog-reader-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setActive(null)}
          >
            <motion.article
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl rounded-3xl border border-border bg-card p-8 sm:p-11 shadow-2xl max-h-[85vh] overflow-y-auto"
            >
              <button data-testid="blog-reader-close-btn" onClick={() => setActive(null)} aria-label="Cerrar artículo" className="absolute top-5 right-5 w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-4 h-4" />
              </button>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-500 font-semibold">{active.category} · {active.date} · {active.readTime}</p>
              <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-3 leading-snug">{active.title}</h3>
              <div className="mt-6 space-y-4">
                {active.content.map((para, i) => (
                  <p key={i} className="text-sm sm:text-base text-muted-foreground leading-relaxed">{para}</p>
                ))}
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
